/**
 * OCR Service - Text Extraction from Images
 * Uses OpenRouter Vision API via Cloudflare Pages Function
 */

export interface OCRResult {
  text: string;
  confidence: number;
  language: string;
}

/**
 * Extract text from an image using OpenRouter Vision AI
 */
export async function extractTextFromImage(
  file: File,
  onProgress?: (progress: number) => void
): Promise<OCRResult> {
  try {
    if (onProgress) onProgress(0.1);

    // Build FormData with the image file
    const formData = new FormData();
    formData.append('file', file);

    const resp = await fetch('/api/extract-text', {
      method: 'POST',
      body: formData,
    });

    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({ error: resp.statusText }));
      throw new Error(errorData.error || `HTTP ${resp.status}`);
    }

    if (onProgress) onProgress(0.9);

    const data = await resp.json();

    if (onProgress) onProgress(1.0);

    return {
      text: data.text || '',
      confidence: data.confidence || 0.95,
      language: data.language || 'eng',
    };
  } catch (error) {
    console.error('[OCR Service] Vision extraction failed:', error);
    throw new Error('Failed to extract text from image');
  }
}

/**
 * Check if a file is an image
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Get supported image formats
 */
export function getSupportedImageFormats(): string[] {
  return ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.webp'];
}